import React, { useContext, useState, useEffect, useRef, useCallback } from 'react'
import { Button, CircularProgress } from '@mui/material'
import { KeyboardBackspace } from '@mui/icons-material'
import { css } from 'styled-components'
import { app } from 'router/routes'
import { DashboardContext } from 'pages/Dashboard/DashboardContext'
import AssessmentFiltersHeader from 'pages/School/Assessments/AssessmentFiltersHeader'
import useFilterRegistry from 'components/FilterManager/hooks/useFilterRegistry'
import { PresentationStyles } from './Style'
import PresentationNavBar from './PresentationNavBar'
import { usePresentationNavigation, usePresentationActions, useSlide2Data, useSlide2GradeGroupAffiliations, useSlide3Data, useSlide4Data, useSlide5Data, useSlide6InterventionData, useStatisticsSideData } from './hooks'
// SlideSchoolComparison / Slide7NormativeGrowth מבוטלים זמנית
import { Slide1Cover, RatingSidePanel, Slide2WhoMeasured, Slide3WhatTested, Slide4ReadingResults, Slide5NextSteps, Slide6Interventions, ExecutionSidePanel, StatisticsSidePanel, MeasurementTotalsPanel } from './slides'
import { getPresentationHeaderTitle, slideTitles, sidePanelEmptyText } from './constants'

// בורר שנה/סמסטר קומפקטי — כדי שגובה שורת הכותרת לא יגדל מעבר למה שהיה לפני שהבורר עבר לכאן
const compactFilterHeaderCss = css`padding: 0px;`

//  הקןמפוננטה רק תצוגה- מידע מהוקס
const PresentationViewer = () => {
    // בורר שנה/סמסטר המצגת משותף עם שאר הדשבורד (אותו state כמו כרטיס "דירוגי הקריאה" הראשי) —
    // כך שהכותרת וכל שקפי המצגת מתעדכנים יחד עם הבחירה
    const { assessmentFilters, setAssessmentFilters, correctWordsPerMinuteDoughnut } = useContext(DashboardContext)
    const filters = useFilterRegistry()
    const { navigateToPage } = usePresentationActions()

    const slide2 = useSlide2Data(assessmentFilters)
    const slide3 = useSlide3Data(assessmentFilters)
    const slide4 = useSlide4Data(assessmentFilters)
    const slide5 = useSlide5Data(assessmentFilters)
    const slide6Intervention = useSlide6InterventionData(assessmentFilters)
    const statisticsSide = useStatisticsSideData(assessmentFilters)

    const goExecutionTracking = () => navigateToPage(app.assessmentExecutionTracking)
    const goStatistics = () => navigateToPage(app.assessmentsStatus)

    // REF מתעדכן בכל רינדןר
    const goNextRef = useRef(null)
    const goStory = useCallback(() => goNextRef.current?.(), [])

    // עמוד -מי נמדד
    // ברירת המחדל זה "תלמידים בתכנית המדידה" בחור (גרף הסה"כ),.
    // הכפתור  "לכל נתוני הביצוע" מנווט למסך של נתןני ביצוע
    const [slide2ActiveCard, setSlide2ActiveCard] = useState('total')

    // כרטיסי "בתי ספר יסודיים" / "חטיבות ביניים" — קריאה נפרדת עם הקבוצה הרלוונטית,  כמו
    // הקריאה "בשלב חינוך" במסך של נתוני ביצוע, 
    const slide2GradeGroup = slide2ActiveCard === 'elementary' ? 'elementary' : slide2ActiveCard === 'middle' ? 'middle' : null
    const slide2GradeGroupData = useSlide2GradeGroupAffiliations(assessmentFilters, slide2GradeGroup)

    const slideConfigs = [
        {
            title: getPresentationHeaderTitle(assessmentFilters?.semester_and_year),
            main: <Slide1Cover onStoryClick={goStory} />,
            side: <RatingSidePanel />,
        },
        {
            title: getPresentationHeaderTitle(assessmentFilters?.semester_and_year),
            main: <Slide2WhoMeasured data={slide2} activeCard={slide2ActiveCard} onShowTotals={setSlide2ActiveCard} onShowAffiliations={setSlide2ActiveCard} />,
            side: slide2ActiveCard === 'total' ?
                <MeasurementTotalsPanel total={slide2.totalStudents} executed={slide2.executedStudents} /> :
                slide2GradeGroup && slide2GradeGroupData.isLoading ?
                    <div className='side-empty'><CircularProgress size={22} color='thirdly' /></div> :
                    <ExecutionSidePanel affiliations={slide2GradeGroup ? slide2GradeGroupData.affiliations : slide2.affiliations} />,
            // רקע תכלת ש"תלמידים בתכנית המדידה" בחור
            sideFlat: slide2ActiveCard === 'total',
            action: { label: 'לכל נתוני הביצוע', onClick: goExecutionTracking },
            isLoading: slide2.isLoading
        },
        {
            title: slideTitles[1],
            subtitle: 'בדקנו עד כמה תשתיות הקריאה מבוססות,\nובאיזו מידה התלמידים יכולים להתמודד עם הטקסטים בלמידה בכיתה.',
            main: <Slide3WhatTested data={slide3} />,
            action: { label: 'לכניסה למסך', disabled: true },
            isLoading: slide3.isLoading
        },
        {
            title: slideTitles[2],
            main: <Slide4ReadingResults data={slide4} />,
            side: <StatisticsSidePanel affiliations={statisticsSide.affiliations} />,
            action: { label: 'לכל נתוני סטטיסטיקות', onClick: goStatistics },
            isLoading: slide4.isLoading
        },
        // עמוד ההשוואה בין בתי ספר — לא רלוונטי כרגע
        // {
        //     title: slideTitles[3],
        //     main: <SlideSchoolComparison />
        // },
        {
            title: slideTitles[4],
            main: <Slide5NextSteps data={slide5} />,
            action: { label: 'לכניסה למסך', disabled: true },
            isLoading: slide5.isLoading
        },
        {
            title: slideTitles[5],
            main: <Slide6Interventions data={slide5} interventionData={slide6Intervention.interventionData} />,
            action: { label: 'לכניסה למסך', disabled: true },
            isLoading: slide5.isLoading || slide6Intervention.isLoading
        },
        // עמוד "האם המענה עובד" — בהמשך
        // {
        //     title: slideTitles[6],
        //     main: <Slide7NormativeGrowth />
        // },
    ]

    const navigation = usePresentationNavigation({ slideCount: slideConfigs.length })
    const currentConfig = slideConfigs[navigation.currentSlide] || {}

    useEffect(() => { goNextRef.current = navigation.next }, [navigation.next])

    return (
        <PresentationStyles>
            <div className='presentation-header'>
                <div>
                    <div className='presentation-title'>{currentConfig.title}</div>
                    {currentConfig.subtitle &&
                        <div className='presentation-subtitle'>{currentConfig.subtitle}</div>}
                </div>
                <div className='presentation-header-controls'>
                    <AssessmentFiltersHeader
                        hideSearchBtn
                        keys={['']}
                        loading={correctWordsPerMinuteDoughnut.isFetching}
                        headerItems={[filters.semester_and_year]}
                        badge
                        refetchData={correctWordsPerMinuteDoughnut.refetch}
                        showLoading={true}
                        assessmentFilters={assessmentFilters}
                        setAssessmentFilters={setAssessmentFilters}
                        rowHeight={36}
                        headercss={compactFilterHeaderCss}
                    />
                    <PresentationNavBar navigation={navigation} slideCount={slideConfigs.length} />
                </div>
            </div>
            <div className='slide-viewport'>
                {currentConfig.isLoading ?
                    <div className='slide-loading'>
                        <CircularProgress color='thirdly' />
                    </div>
                    :
                    <>
                        <div className='slide-body'>
                            <div className='slide-main'>
                                {currentConfig.main}
                            </div>
                            <div className={`slide-side ${currentConfig.side ? '' : 'empty-side'} ${currentConfig.sideFlat ? 'totals-view' : ''}`}>
                                {currentConfig.side ?
                                    <div className={`slide-side-inner ${currentConfig.sideFlat ? 'totals-view' : ''}`}>
                                        {currentConfig.side}
                                    </div>
                                    :
                                    <div className='side-empty'>{sidePanelEmptyText}</div>}
                            </div>
                        </div>
                        {currentConfig.action &&
                            <div className='slide-action-row'>
                                <Button className='slide-action-btn' onClick={currentConfig.action.onClick} disabled={currentConfig.action.disabled} type='button'>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span>{currentConfig.action.label}</span>
                                        <KeyboardBackspace fontSize='small' />
                                    </span>
                                </Button>
                            </div>}
                    </>}
            </div>
        </PresentationStyles>
    )
}

export default PresentationViewer
